import React from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Mail, MapPin, Instagram, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useRegion } from '../context/RegionContext';
import { translations } from '../translations/translations';

const Footer = () => {
  const { language } = useLanguage();
  const { getLink, regionData, region } = useRegion();
  const t = translations[language] || translations.pt;
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative overflow-hidden" data-testid="footer">
      {/* Wave SVG Separator */}
      <div className="absolute top-0 left-0 w-full overflow-hidden leading-none">
        <svg 
          className="relative block w-full h-16 md:h-24" 
          viewBox="0 0 1200 120" 
          preserveAspectRatio="none"
        >
          <path 
            d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" 
            className="fill-white"
          />
        </svg>
      </div>

      {/* Main Footer with Gradient */}
      <div className="bg-gradient-to-b from-[#1a5c3a] via-[#15472d] to-[#0d2e1d] pt-24 md:pt-32 pb-8">
        {/* Decorative circles */}
        <div className="absolute top-20 left-10 w-64 h-64 bg-white/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#d4a84b]/10 rounded-full blur-3xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Top Section - Logo and CTA */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-16 pb-12 border-b border-white/10">
            <div className="mb-8 md:mb-0">
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_trusted-maintenance-1/artifacts/l9fyih4f_Whisk_4dd066d44f7c3e88b4a47b0cc96820dddr-removebg-preview.png" 
                  alt="Sancur Services" 
                  className="h-16 w-auto brightness-0 invert"
                />
                <span className="text-[#d4a84b] font-heading text-2xl font-bold tracking-tight">
                  Sancur Services
                </span>
              </div>
              <p className="text-white/70 font-body max-w-md">
                {regionData.description?.[language] || regionData.description?.en || t.footer.description}
              </p>
            </div>
            
            <Link 
              to="/ie/quote"
              className="group inline-flex items-center gap-3 px-6 py-3 bg-white text-[#1a5c3a] font-semibold rounded-full hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl"
            >
              {t.nav.contact}
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* Main Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
            {/* Quick Links */}
            <div>
              <h4 className="text-white font-heading font-semibold mb-6 flex items-center gap-2">
                <span className="w-8 h-0.5 bg-[#d4a84b]"></span>
                {t.footer.quickLinks}
              </h4>
              <nav className="space-y-3">
                <Link 
                  to={getLink('/')} 
                  className="block text-white/70 hover:text-white hover:translate-x-1 transition-all font-body"
                  data-testid="footer-link-home"
                >
                  {t.nav.home}
                </Link>
                <Link 
                  to={getLink('/services')} 
                  className="block text-white/70 hover:text-white hover:translate-x-1 transition-all font-body"
                  data-testid="footer-link-services"
                >
                  {t.nav.services}
                </Link>
                <Link 
                  to={getLink('/gallery')} 
                  className="block text-white/70 hover:text-white hover:translate-x-1 transition-all font-body"
                >
                  {t.nav.gallery}
                </Link>
                <Link 
                  to={getLink('/about')} 
                  className="block text-white/70 hover:text-white hover:translate-x-1 transition-all font-body"
                  data-testid="footer-link-about"
                >
                  {t.nav.about}
                </Link>
              </nav>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-white font-heading font-semibold mb-6 flex items-center gap-2">
                <span className="w-8 h-0.5 bg-[#d4a84b]"></span>
                {t.nav.services}
              </h4>
              <nav className="space-y-3">
                {region === 'ie' ? (
                  <>
                    <span className="block text-white/70 font-body">
                      {language === 'pt' ? 'Limpeza Regular' : 'Regular House Cleaning'}
                    </span>
                    <span className="block text-white/70 font-body">
                      {language === 'pt' ? 'Limpeza Profunda' : 'Deep Cleaning'}
                    </span>
                    <span className="block text-white/70 font-body">
                      {language === 'pt' ? 'Limpeza de Escritório' : 'Office Cleaning'}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="block text-white/70 font-body">
                      {language === 'pt' ? 'Pintura' : 'Painting'}
                    </span>
                    <span className="block text-white/70 font-body">
                      {language === 'pt' ? 'Limpeza Doméstica' : 'House Cleaning'}
                    </span>
                    <span className="block text-white/70 font-body">
                      {language === 'pt' ? 'Pequenos Reparos' : 'Small Repairs'}
                    </span>
                    <span className="block text-white/70 font-body">
                      {language === 'pt' ? 'Manutenção Geral' : 'General Maintenance'}
                    </span>
                  </>
                )}
              </nav>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-white font-heading font-semibold mb-6 flex items-center gap-2">
                <span className="w-8 h-0.5 bg-[#d4a84b]"></span>
                {t.contact.info.title}
              </h4>
              <div className="space-y-4">
                <a 
                  href={`https://wa.me/${regionData.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-white/70 hover:text-white transition-colors group"
                >
                  <div className="w-10 h-10 bg-[#25D366] rounded-full flex items-center justify-center group-hover:bg-[#20BA5A] transition-colors">
                    <MessageCircle size={18} />
                  </div>
                  <span className="font-body">{regionData.phone} (WhatsApp)</span>
                </a>
                <a 
                  href={`mailto:${regionData.email}`}
                  className="flex items-center gap-3 text-white/70 hover:text-white transition-colors group"
                >
                  <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center group-hover:bg-[#d4a84b] transition-colors">
                    <Mail size={18} />
                  </div>
                  <span className="font-body">{regionData.email}</span>
                </a>
                <div className="flex items-center gap-3 text-white/70">
                  <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
                    <MapPin size={18} />
                  </div>
                  <span className="font-body">{regionData.area}</span>
                </div>
              </div>
            </div>

            {/* Social & Area */}
            <div>
              <h4 className="text-white font-heading font-semibold mb-6 flex items-center gap-2">
                <span className="w-8 h-0.5 bg-[#d4a84b]"></span>
                {language === 'pt' ? 'Siga-nos' : 'Follow Us'}
              </h4>
              <div className="flex gap-3 mb-6">
                <a 
                  href="https://www.facebook.com/sancurservices"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#1877F2] transition-colors group"
                  aria-label="Facebook"
                >
                  <svg className="w-5 h-5 text-white/70 group-hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a 
                  href="https://www.instagram.com/sancurservices"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center hover:bg-gradient-to-br hover:from-[#f09433] hover:via-[#e6683c] hover:to-[#dc2743] transition-colors group"
                  aria-label="Instagram"
                >
                  <Instagram size={22} className="text-white/70 group-hover:text-white" />
                </a>
                <a 
                  href={`https://wa.me/${regionData.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#25D366] transition-colors group"
                  aria-label="WhatsApp"
                >
                  <svg className="w-5 h-5 text-white/70 group-hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </a>
              </div>
              
              {/* Staff Area Link */}
              <Link 
                to="/staff/login"
                className="inline-flex items-center gap-2 text-white/70 hover:text-[#d4a84b] transition-colors font-body"
              >
                <span>{language === 'pt' ? 'Área Staff' : 'Staff Area'}</span>
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-white/10">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-white/50 font-body text-sm">
                © {currentYear} Sancur Property Services. {t.footer.rights}
              </p>
              
              {/* Legal Links */}
              <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
                <Link to="/ie/privacy" className="text-white/50 hover:text-white text-sm font-body transition-colors">
                  {language === 'pt' ? 'Política de Privacidade' : 'Privacy Policy'}
                </Link>
                <span className="text-white/30 hidden sm:inline">|</span>
                <Link to="/ie/terms" className="text-white/50 hover:text-white text-sm font-body transition-colors">
                  {language === 'pt' ? 'Termos e Condições' : 'Terms & Conditions'}
                </Link>
                <span className="text-white/30 hidden sm:inline">|</span>
                <Link to="/ie/cookies" className="text-white/50 hover:text-white text-sm font-body transition-colors">
                  {language === 'pt' ? 'Política de Cookies' : 'Cookie Policy'}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
